import { api } from '../apiClient';
import {
  ScannerDashboard,
  ScannerEvent,
  ScannerActivityPlace,
  PassScanResult,
  AddonScanResult,
  ActivityScanResult,
  EventStats,
  AttendeesResponse,
  AddonStats,
  AddonOrdersResponse,
} from '../../types';

export const scannerService = {
  /**
   * Get scanner dashboard with available events and activity places
   */
  getDashboard: async (): Promise<ScannerDashboard> => {
    const response = await api.get('/scanner/dashboard/');
    return response.data;
  },

  /**
   * Get event details for scanning
   */
  getEventScannerData: async (eventSlug: string): Promise<{ success: boolean; event: ScannerEvent }> => {
    const response = await api.get(`/scanner/event/${eventSlug}/`);
    return response.data;
  },

  /**
   * Scan a pass (QR/barcode)
   */
  scanPass: async (eventSlug: string, identifier: string): Promise<PassScanResult> => {
    const response = await api.post(`/scanner/event/${eventSlug}/scan/`, {
      uuid: identifier,
    });
    return response.data;
  },

  /**
   * Get event scanning statistics
   */
  getEventStats: async (eventSlug: string): Promise<EventStats> => {
    const response = await api.get(`/scanner/event/${eventSlug}/stats/`);
    return response.data;
  },

  /**
   * Get paginated attendees list with search and filters
   */
  getEventAttendees: async (
    eventSlug: string,
    params: {
      search?: string;
      category?: string;
      status?: 'scanned' | 'pending' | '';
      page?: number;
    } = {}
  ): Promise<AttendeesResponse> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page.toString());

    const response = await api.get(`/scanner/event/${eventSlug}/attendees/?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Manually scan an attendee from the attendees list
   */
  scanAttendee: async (eventSlug: string, passId: string): Promise<{ success: boolean; message: string; pass: any }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/attendees/${passId}/scan/`);
    return response.data;
  },

  /**
   * Unscan an attendee (requires unscan permission)
   */
  unscanAttendee: async (eventSlug: string, passId: string): Promise<{ success: boolean; message: string; pass: any }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/attendees/${passId}/unscan/`);
    return response.data;
  },

  /**
   * Resend pass email to attendee
   */
  resendPassEmail: async (eventSlug: string, passId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/attendees/${passId}/resend-email/`);
    return response.data;
  },

  /**
   * Scan a wristband
   */
  scanWristband: async (eventSlug: string, identifier: string): Promise<{ success: boolean; message: string; id: string; name: string }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/wristband/scan/`, {
      uuid: identifier,
    });
    return response.data;
  },

  /**
   * Scan an addon order
   */
  scanAddon: async (eventSlug: string, token: string): Promise<AddonScanResult> => {
    const response = await api.post(`/scanner/event/${eventSlug}/addon/scan/`, {
      uuid: token,
    });
    return response.data;
  },

  /**
   * Get addon delivery statistics
   */
  getAddonStats: async (eventSlug: string): Promise<AddonStats> => {
    const response = await api.get(`/scanner/event/${eventSlug}/addon/stats/`);
    return response.data;
  },

  /**
   * Get paginated addon orders list with search and filters
   */
  getAddonOrders: async (
    eventSlug: string,
    params: {
      search?: string;
      addon?: string;
      status?: 'delivered' | 'pending' | '';
      page?: number;
    } = {}
  ): Promise<AddonOrdersResponse> => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.addon) queryParams.append('addon', params.addon);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page.toString());

    const response = await api.get(`/scanner/event/${eventSlug}/addon/orders/?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Mark addon order as delivered
   */
  deliverAddon: async (eventSlug: string, orderId: number): Promise<{ success: boolean; message: string; order: any }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/addon/orders/${orderId}/deliver/`);
    return response.data;
  },

  /**
   * Unmark addon order as delivered (requires unscan permission)
   */
  undeliverAddon: async (eventSlug: string, orderId: number): Promise<{ success: boolean; message: string; order: any }> => {
    const response = await api.post(`/scanner/event/${eventSlug}/addon/orders/${orderId}/undeliver/`);
    return response.data;
  },

  /**
   * Get activity place details for scanning
   */
  getActivityPlaceScannerData: async (activityPlaceSlug: string): Promise<{ success: boolean; activity_place: ScannerActivityPlace }> => {
    const response = await api.get(`/scanner/activity/${activityPlaceSlug}/`);
    return response.data;
  },

  /**
   * Scan an activity booking
   */
  scanActivityBooking: async (activityPlaceSlug: string, identifier: string): Promise<ActivityScanResult> => {
    const response = await api.post(`/scanner/activity/${activityPlaceSlug}/scan/`, {
      uuid: identifier,
    });
    return response.data;
  },
};

